import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, UserPlus, Trash2, Home, Target, Mail, User, Lock, Shield, AlertTriangle, Users } from "lucide-react";
import { useLanguage } from "@/i18n";
import { EvaluatorCard } from "@/components/evaluators/EvaluatorCard";
import { LinkPlayerDialog, CreateFromPlayerDialog, EditEvaluatorDialog } from "@/components/evaluators/EvaluatorDialogs";

const ROLES = [
  { value: "admin", label: "Administrador", description: "Acesso total" },
  { value: "tecnico", label: "Técnico", description: "Times, estatísticas, jogadores" },
  { value: "avaliador", label: "Avaliador", description: "Apenas avaliar" },
];

export default function EvaluatorsPage({ evaluator, onLogout }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [evaluators, setEvaluators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [players, setPlayers] = useState([]);
  const [globalReferencePlayer, setGlobalReferencePlayer] = useState("");
  const [setterReferencePlayer, setSetterReferencePlayer] = useState("");
  const [newEvaluator, setNewEvaluator] = useState({ name: "", email: "", password: "", role: "avaliador" });
  const [selectedEvaluators, setSelectedEvaluators] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // Dialog states
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkingEvaluator, setLinkingEvaluator] = useState(null);
  const [isLinking, setIsLinking] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEvaluator, setEditingEvaluator] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [createFromPlayerOpen, setCreateFromPlayerOpen] = useState(false);
  const [isCreatingFromPlayer, setIsCreatingFromPlayer] = useState(false);

  useEffect(() => {
    fetchEvaluators();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvaluators = async () => {
    try {
      const [evaluatorsRes, playersRes, globalRefRes, setterRefRes] = await Promise.all([
        axios.get(`${API}/evaluators`),
        axios.get(`${API}/players`),
        axios.get(`${API}/global-reference`),
        axios.get(`${API}/setter-reference`)
      ]);
      setEvaluators(evaluatorsRes.data);
      setPlayers(playersRes.data);
      setGlobalReferencePlayer(globalRefRes.data?.reference_player_id || "");
      setSetterReferencePlayer(setterRefRes.data?.reference_setter_id || "");
    } catch (error) {
      toast.error(t('toasts.errorLoadingEvaluators'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddEvaluator = async (e) => {
    e.preventDefault();
    if (!newEvaluator.name || !newEvaluator.email || !newEvaluator.password) {
      toast.error(t('toasts.fillAllFields'));
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/evaluators/register`, newEvaluator);
      setEvaluators([...evaluators, response.data]);
      setNewEvaluator({ name: "", email: "", password: "", role: "avaliador" });
      toast.success(t('toasts.evaluatorCreated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorRegisteringEvaluator'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (evalId, currentStatus) => {
    try {
      await axios.put(`${API}/evaluators/${evalId}`, { is_active: !currentStatus });
      setEvaluators(evaluators.map(e => e.id === evalId ? { ...e, is_active: !currentStatus } : e));
      toast.success(!currentStatus ? t('toasts.evaluatorActivated') : t('toasts.evaluatorDeactivated'));
    } catch (error) {
      toast.error(t('toasts.errorUpdatingPlayer'));
    }
  };

  const handleChangeRole = async (evalId, newRole) => {
    try {
      await axios.put(`${API}/evaluators/${evalId}`, { role: newRole });
      setEvaluators(evaluators.map(e => e.id === evalId ? { ...e, role: newRole } : e));
      toast.success(t('toasts.roleUpdated'));
    } catch (error) {
      toast.error(t('toasts.errorUpdatingPlayer'));
    }
  };

  const handleDeleteEvaluator = async (evalId, evalName) => {
    if (!window.confirm(`Tem certeza que deseja excluir ${evalName}?`)) return;
    try {
      await axios.delete(`${API}/evaluators/${evalId}`);
      setEvaluators(evaluators.filter(e => e.id !== evalId));
      setSelectedEvaluators(selectedEvaluators.filter(id => id !== evalId));
      toast.success(t('toasts.evaluatorDeleted'));
    } catch (error) {
      toast.error(t('toasts.errorDeletingEvaluators'));
    }
  };

  const toggleEvaluatorSelection = (evalId) => {
    if (evalId === evaluator.id) return;
    setSelectedEvaluators(prev => prev.includes(evalId) ? prev.filter(id => id !== evalId) : [...prev, evalId]);
  };

  const selectableEvaluators = evaluators.filter(e => e.id !== evaluator.id);
  const isAllSelected = selectableEvaluators.length > 0 && selectedEvaluators.length === selectableEvaluators.length;
  const isSomeSelected = selectedEvaluators.length > 0 && selectedEvaluators.length < selectableEvaluators.length;

  const toggleSelectAll = () => {
    setSelectedEvaluators(isAllSelected ? [] : selectableEvaluators.map(e => e.id));
  };

  const handleBulkDelete = async () => {
    if (selectedEvaluators.length === 0) return;
    setIsDeleting(true);
    try {
      const response = await axios.post(`${API}/evaluators/bulk-delete`, { evaluator_ids: selectedEvaluators });
      setEvaluators(evaluators.filter(e => !selectedEvaluators.includes(e.id)));
      toast.success(response.data.message);
      setSelectedEvaluators([]);
    } catch (error) {
      toast.error(t('toasts.errorDeletingEvaluators'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLinkPlayer = async (playerId, password) => {
    if (!playerId || !password) { toast.error(t('toasts.selectPlayer') + " " + t('toasts.enterNewPassword')); return; }
    setIsLinking(true);
    try {
      await axios.post(`${API}/evaluators/${linkingEvaluator.id}/link-player`, { player_id: playerId, password });
      toast.success(t('toasts.linkedPlayer'));
      setLinkModalOpen(false);
      fetchEvaluators();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorLinking'));
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlinkPlayer = async (evalId, evalName) => {
    if (!window.confirm(`Tem certeza que deseja desvincular ${evalName} do jogador?`)) return;
    try {
      await axios.post(`${API}/evaluators/${evalId}/unlink-player`);
      toast.success(t('toasts.unlinkedPlayer'));
      fetchEvaluators();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorUnlinking'));
    }
  };

  const handleSaveEdit = async (name, email, password) => {
    if (!name.trim() || !email.trim()) { toast.error("Nome e email são obrigatórios"); return; }
    setIsSavingEdit(true);
    try {
      const updateData = { name: name.trim(), email: email.trim() };
      if (password.trim()) {
        if (password.length < 4) { toast.error("A senha deve ter pelo menos 4 caracteres"); setIsSavingEdit(false); return; }
        updateData.password = password;
      }
      await axios.put(`${API}/evaluators/${editingEvaluator.id}`, updateData);
      toast.success("Avaliador atualizado com sucesso!");
      setEditModalOpen(false);
      setEditingEvaluator(null);
      fetchEvaluators();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar avaliador");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleCreateFromPlayer = async (playerId, email, password, role) => {
    const player = players.find(p => p.id === playerId);
    if (!player) { toast.error("Jogador não encontrado"); return; }
    const playerName = player.first_name ? `${player.first_name} ${player.last_name}` : player.name;
    setIsCreatingFromPlayer(true);
    try {
      await axios.post(`${API}/players/${playerId}/make-evaluator`, { email, password, name: playerName, role });
      toast.success(`${playerName} ${t('toasts.madeEvaluator')} ${role}!`);
      setCreateFromPlayerOpen(false);
      fetchEvaluators();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorCreatingEvaluator'));
    } finally {
      setIsCreatingFromPlayer(false);
    }
  };

  const handleSetReference = async (type, value) => {
    try {
      const refValue = value === 'none' ? null : value;
      if (type === 'global') {
        setGlobalReferencePlayer(refValue || "");
        await axios.put(`${API}/global-reference`, { reference_player_id: refValue });
        toast.success(t('toasts.referencePlayerUpdated'));
      } else {
        setSetterReferencePlayer(refValue || "");
        await axios.put(`${API}/setter-reference`, { reference_setter_id: refValue });
        toast.success(t('toasts.referenceSetterUpdated'));
      }
    } catch (error) {
      toast.error(t('toasts.errorSavingReference'));
    }
  };

  const getPlayerName = (p) => p.first_name ? `${p.first_name} ${p.last_name}` : p.name;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4 justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">Gerenciar Avaliadores</h1>
              </div>
            </div>

            {evaluator?.role === 'admin' && (
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground whitespace-nowrap font-medium">Jogador Parametro:</Label>
                  <Select value={globalReferencePlayer || 'none'} onValueChange={(v) => handleSetReference('global', v)}>
                    <SelectTrigger className="bg-input/50 border-border flex-1 sm:max-w-xs"><SelectValue placeholder="Nenhum selecionado" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {players.filter(p => p.position !== 'Levantador').map((p) => (
                        <SelectItem key={p.id} value={p.id}>{getPlayerName(p)} ({p.position})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-yellow-500/5 rounded-lg border border-yellow-500/20">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground whitespace-nowrap font-medium">Levantador Parametro:</Label>
                  <Select value={setterReferencePlayer || 'none'} onValueChange={(v) => handleSetReference('setter', v)}>
                    <SelectTrigger className="bg-input/50 border-border flex-1 sm:max-w-xs"><SelectValue placeholder="Nenhum selecionado" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {players.filter(p => p.position === 'Levantador').map((p) => (
                        <SelectItem key={p.id} value={p.id}>{getPlayerName(p)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 pb-24 md:pb-8">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Add Evaluator Form */}
          <Card className="bg-card border-border/50 shadow-xl lg:col-span-1">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold tracking-tight flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-primary" /> Cadastrar Avaliador
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddEvaluator} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Nome Completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input type="text" placeholder="Nome do avaliador" className="pl-10 bg-input/50 border-transparent focus:border-primary h-12"
                      value={newEvaluator.name} onChange={(e) => setNewEvaluator({ ...newEvaluator, name: e.target.value })} required data-testid="evaluator-name-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input type="email" placeholder="email@exemplo.com" className="pl-10 bg-input/50 border-transparent focus:border-primary h-12"
                      value={newEvaluator.email} onChange={(e) => setNewEvaluator({ ...newEvaluator, email: e.target.value })} required data-testid="evaluator-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input type="password" placeholder="••••••••" className="pl-10 bg-input/50 border-transparent focus:border-primary h-12"
                      value={newEvaluator.password} onChange={(e) => setNewEvaluator({ ...newEvaluator, password: e.target.value })} required data-testid="evaluator-password-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Nível de Acesso</Label>
                  <Select value={newEvaluator.role} onValueChange={(v) => setNewEvaluator({ ...newEvaluator, role: v })}>
                    <SelectTrigger className="bg-input/50 h-12"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map(role => (
                        <SelectItem key={role.value} value={role.value}>
                          <div>
                            <span className="font-medium">{role.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">({role.description})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,122,255,0.3)] font-bold uppercase tracking-wider h-12"
                  disabled={isSubmitting} data-testid="add-evaluator-btn">
                  <UserPlus className="mr-2 h-4 w-4" /> {isSubmitting ? "Cadastrando..." : "Cadastrar"}
                </Button>
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border/50" /></div>
                  <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">ou</span></div>
                </div>
                <Button type="button" variant="outline" className="w-full border-green-500/50 text-green-500 hover:bg-green-500/10 h-12"
                  onClick={() => setCreateFromPlayerOpen(true)} data-testid="create-from-player-btn">
                  <Users className="mr-2 h-4 w-4" /> Criar a partir de Jogador
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Evaluators List */}
          <Card className="bg-card border-border/50 shadow-xl lg:col-span-2">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className="text-lg font-bold tracking-tight flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" /> Avaliadores ({evaluators.length})
                </CardTitle>
                {selectedEvaluators.length > 0 && evaluator?.role === 'admin' && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" disabled={isDeleting} data-testid="bulk-delete-evaluators-btn" className="w-full sm:w-auto">
                        <Trash2 className="mr-2 h-4 w-4" /> Excluir ({selectedEvaluators.length})
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-card border-border">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-destructive" /> Confirmar Exclusão
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir <strong>{selectedEvaluators.length} avaliador(es)</strong>?<br /><br />Esta ação não pode ser desfeita.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          {isDeleting ? "Excluindo..." : "Sim, Excluir"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">Carregando...</div>
              ) : evaluators.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Nenhum avaliador cadastrado ainda.</div>
              ) : (
                <div className="space-y-3">
                  {evaluator?.role === 'admin' && selectableEvaluators.length > 0 && (
                    <div className="flex items-center gap-2 p-3 bg-secondary/30 rounded-lg">
                      <Checkbox checked={isAllSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-evaluators"
                        className={isSomeSelected ? "data-[state=checked]:bg-primary/50" : ""} />
                      <span className="text-sm text-muted-foreground">
                        {selectedEvaluators.length > 0 ? `${selectedEvaluators.length} selecionado(s)` : "Selecionar todos"}
                      </span>
                    </div>
                  )}
                  {evaluators.map((eval_, index) => (
                    <EvaluatorCard
                      key={eval_.id}
                      eval_={eval_}
                      index={index}
                      evaluator={evaluator}
                      players={players}
                      selectedEvaluators={selectedEvaluators}
                      onToggleSelection={toggleEvaluatorSelection}
                      onToggleActive={handleToggleActive}
                      onChangeRole={handleChangeRole}
                      onDelete={handleDeleteEvaluator}
                      onLinkPlayer={(e) => { setLinkingEvaluator(e); setLinkModalOpen(true); }}
                      onUnlinkPlayer={handleUnlinkPlayer}
                      onEdit={(e) => { setEditingEvaluator(e); setEditModalOpen(true); }}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Dialogs */}
      <LinkPlayerDialog
        open={linkModalOpen}
        onOpenChange={setLinkModalOpen}
        evaluatorName={linkingEvaluator?.name}
        players={players}
        onLink={handleLinkPlayer}
        isLinking={isLinking}
      />
      <CreateFromPlayerDialog
        open={createFromPlayerOpen}
        onOpenChange={setCreateFromPlayerOpen}
        players={players}
        onCreate={handleCreateFromPlayer}
        isCreating={isCreatingFromPlayer}
      />
      <EditEvaluatorDialog
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        evaluatorData={editingEvaluator}
        onSave={handleSaveEdit}
        isSaving={isSavingEdit}
      />

      {/* Mobile Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-md border-t border-border/50 py-2 px-4 md:hidden z-50">
        <div className="flex justify-around">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="flex flex-col items-center gap-1 h-auto py-2 min-w-[60px]">
            <Home className="h-5 w-5" /><span className="text-xs">Início</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/evaluators')} className="flex flex-col items-center gap-1 h-auto py-2 text-primary min-w-[60px]">
            <Shield className="h-5 w-5" /><span className="text-xs">Avaliadores</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/evaluate')} className="flex flex-col items-center gap-1 h-auto py-2 min-w-[60px]">
            <Target className="h-5 w-5" /><span className="text-xs">Avaliar</span>
          </Button>
        </div>
      </nav>
    </div>
  );
}
